import type { ThreatElementDocument } from '../../api-utils/elements';
import type { SecurityEventItem } from '../../api-utils/security_events';
import { campaignMemberActionIds } from '../../lib/funnel';
import type { ForceGraphNode } from './correlationGraph';

export function campaignByMemberActionId(
  threatElements: ThreatElementDocument[]
): Map<string, ThreatElementDocument> {
  const map = new Map<string, ThreatElementDocument>();
  for (const el of threatElements) {
    if (String(el.element_type || '').toLowerCase() !== 'campaign') continue;
    for (const memberId of campaignMemberActionIds(el)) {
      if (memberId) map.set(memberId, el);
    }
  }
  return map;
}

export function siblingActionIds(actionId: string, campaign: ThreatElementDocument | undefined): string[] {
  if (!actionId || !campaign) return [];
  return campaignMemberActionIds(campaign).filter((id) => id && id !== actionId);
}

function isActionNode(node: Pick<ForceGraphNode, 'layer' | 'universalType'>): boolean {
  return node.layer === 'threat' && String(node.universalType || '').toLowerCase() === 'action';
}

/** Attack order for action diamonds (campaign members). Empty when fewer than 2. */
export function attackPathNumbers(
  nodes: Array<Pick<ForceGraphNode, 'id' | 'layer' | 'universalType'>>,
  timeByNodeId: Map<string, number>
): Map<string, number> {
  const actions = nodes.filter(isActionNode);
  if (actions.length < 2) return new Map();
  const ranked = [...actions].sort((a, b) => {
    const ta = timeByNodeId.get(a.id);
    const tb = timeByNodeId.get(b.id);
    const na = ta == null || !Number.isFinite(ta) ? Number.POSITIVE_INFINITY : ta;
    const nb = tb == null || !Number.isFinite(tb) ? Number.POSITIVE_INFINITY : tb;
    if (na !== nb) return na - nb;
    return a.id.localeCompare(b.id);
  });
  return new Map(ranked.map((node, index) => [node.id, index + 1]));
}

const NODE_TOKEN_KEYS: Array<keyof Pick<ForceGraphNode, 'id' | 'label' | 'name' | 'externalId'>> = [
  'id',
  'label',
  'name',
  'externalId',
];

export function tokensFromGraphNode(node: Pick<ForceGraphNode, 'id' | 'label' | 'name' | 'externalId'>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of NODE_TOKEN_KEYS) {
    const raw = String(node[key] || '')
      .trim()
      .toLowerCase();
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

function eventHaystack(event: SecurityEventItem): string {
  return [
    event.actor_id,
    event.actor_identifier,
    event.actor_name,
    event.source_ip,
    event.destination_ip,
    event.resource_id,
    event.resource_identifier,
    event.resource_name,
    event.resource_type,
    event.target_identifier,
    event.target_name,
    event.target_type,
    event.event_id,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
}

export function securityEventMentionsNode(
  event: SecurityEventItem,
  node: Pick<ForceGraphNode, 'id' | 'label' | 'name' | 'externalId'>
): boolean {
  const hay = eventHaystack(event);
  if (!hay) return false;
  return tokensFromGraphNode(node).some((token) => token.length > 0 && hay.includes(token));
}
